import * as React from 'react';
import { Message, ToolCallState } from './types';
import AiChatDataCard from './AiChatDataCard';
import AiChatConfirm from './AiChatConfirm';
import { MarkdownRenderer } from './MarkdownRenderer';
import AiAvatar from './AiAvatar';
import WidgetRenderer from './widgets/WidgetRenderer';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import { stripQuickActionsComment } from './stripQuickActionsComment';
import AiChatSourceLinks from './AiChatSourceLinks';
import AiChatProgress from './AiChatProgress';
import { extractSourceCitations } from './sourceCitations';

type Props = {
  message: Message;
  pendingConfirm: ToolCallState | null;
  // True only for the assistant message currently being written.
  isStreaming?: boolean;
  activeToolName?: string | null;
  onConfirm: (toolId: string) => void;
  onCancel: (toolId: string) => void;
  onNavigate?: (route: string) => void;
  onRetry?: () => void;
  onFeedback?: (
    messageId: string,
    rating: 'up' | 'down',
    comment?: string,
  ) => void;
};

const AiChatMessage: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  pendingConfirm,
  isStreaming = false,
  activeToolName = null,
  onConfirm,
  onCancel,
  onNavigate,
  onRetry,
  onFeedback,
}) => {
  const isUser = message.role === 'user';
  const raw = message.content ? stripQuickActionsComment(message.content) : '';
  // Citations are lifted out of the markdown so they render as chips instead of
  // hyperlinks. User messages are never parsed — only the assistant cites.
  const { body, sources } = isUser
    ? { body: raw, sources: [] }
    : extractSourceCitations(raw);
  const content = body;

  const hasConfirmForThisMessage =
    pendingConfirm &&
    message.toolCalls.some((tc) => tc.id === pendingConfirm.id);

  // A widget or data card is the thing that renders at its own narrow width and
  // then jumps once the prose arrives, so its bubble claims the column up front.
  const hasCard =
    (message.widgets ?? []).length > 0 || message.dataCards.length > 0;

  // A reply that is nothing but citations still has something to show.
  const hasBubbleContent =
    !!content ||
    sources.length > 0 ||
    message.dataCards.length > 0 ||
    (message.widgets ?? []).length > 0 ||
    !!hasConfirmForThisMessage;

  // An assistant turn that has produced nothing yet still renders, because the
  // progress trail is what fills that gap. Once the turn is over there is no
  // trail — AiChatProgress unmounts when streaming ends — so an empty turn is
  // an empty column with a lone avatar in it, whether or not tools ran.
  if (!isUser && !hasBubbleContent && !isStreaming) {
    return null;
  }

  const canFeedback =
    !isUser &&
    !!onFeedback &&
    !!message.runId &&
    !hasConfirmForThisMessage &&
    (content.length > 0 ||
      sources.length > 0 ||
      (message.widgets ?? []).length > 0);

  return (
    <div className={`ai-chat-msg-row${isUser ? ' ai-chat-msg-row--user' : ''}`}>
      {!isUser && <AiAvatar size="sm" />}
      {/* The progress trail sits under the bubble, not inside it: it reports on
          the answer rather than being part of it. The column holds a stable
          width so a widget that arrives before the prose doesn't render narrow
          and then jump wider mid-stream. */}
      <div
        className={`ai-chat-msg-column${
          isUser ? ' ai-chat-msg-column--user' : ''
        }`}>
        {hasBubbleContent && (
          <div
            className={
              isUser
                ? 'ai-chat-msg-user'
                : `ai-chat-msg-assistant ai-chat-markdown${
                    hasCard ? ' ai-chat-msg-assistant--wide' : ''
                  }`
            }>
            {(message.widgets ?? []).map((widget, idx) => (
              <WidgetRenderer key={`widget-${idx}`} widget={widget} />
            ))}

            {hasConfirmForThisMessage && pendingConfirm && (
              <AiChatConfirm
                toolCall={pendingConfirm}
                onConfirm={onConfirm}
                onCancel={onCancel}
              />
            )}

            {content && !hasConfirmForThisMessage && (
              <MarkdownRenderer>{content}</MarkdownRenderer>
            )}

            {!hasConfirmForThisMessage && (
              <AiChatSourceLinks sources={sources} onNavigate={onNavigate} />
            )}

            {(message.widgets ?? []).length === 0 &&
              message.dataCards.map((card, idx) => (
                <AiChatDataCard key={idx} dataCard={card} />
              ))}
          </div>
        )}

        {!isUser && !hasConfirmForThisMessage && (
          <AiChatProgress
            isStreaming={isStreaming}
            activeToolName={activeToolName}
            toolCalls={message.toolCalls}
            streamedLength={content.length}
            onRetry={onRetry}
          />
        )}

        {canFeedback && (
          <FeedbackControls
            message={message}
            onFeedback={onFeedback as NonNullable<Props['onFeedback']>}
          />
        )}
      </div>
    </div>
  );
};

type FeedbackControlsProps = {
  message: Message;
  onFeedback: NonNullable<Props['onFeedback']>;
};

const FeedbackControls: React.FC<
  React.PropsWithChildren<FeedbackControlsProps>
> = ({ message, onFeedback }) => {
  const [showCommentInput, setShowCommentInput] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const rating = message.feedback;

  const handleThumb = (value: 'up' | 'down') => {
    if (rating) return;
    onFeedback(message.id, value);
    if (value === 'down') setShowCommentInput(true);
  };

  const submitComment = () => {
    const trimmed = comment.trim();
    if (trimmed.length > 0) {
      onFeedback(message.id, 'down', trimmed);
    }
    setShowCommentInput(false);
  };

  return (
    <div className="ai-chat-feedback" data-rated={rating ?? ''}>
      {(!rating || rating === 'up') && (
        <button
          type="button"
          aria-label="Helpful response"
          aria-pressed={rating === 'up'}
          className={`ai-chat-feedback-btn${
            rating === 'up' ? ' ai-chat-feedback-btn--active-up' : ''
          }`}
          onClick={() => handleThumb('up')}
          disabled={!!rating}>
          <FlairIcon icon="phosphor-thumbs-up" />
        </button>
      )}
      {(!rating || rating === 'down') && (
        <button
          type="button"
          aria-label="Unhelpful response"
          aria-pressed={rating === 'down'}
          className={`ai-chat-feedback-btn${
            rating === 'down' ? ' ai-chat-feedback-btn--active-down' : ''
          }`}
          onClick={() => handleThumb('down')}
          disabled={!!rating}>
          <FlairIcon icon="phosphor-thumbs-down" />
        </button>
      )}
      {showCommentInput && (
        <input
          type="text"
          autoFocus
          className="ai-chat-feedback-comment"
          placeholder="What went wrong?"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          onBlur={submitComment}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submitComment();
            if (e.key === 'Escape') setShowCommentInput(false);
          }}
        />
      )}
    </div>
  );
};

export default AiChatMessage;
